import type { InputHTMLAttributes } from "react";

import Image from "next/image";
import { forwardRef } from "react";

import { mergeClasses } from "@trex-2b/libs";

/**
 * Figma: StandardDesign `StartPrismInput` COMPONENT_SET — node 8875:3734 / frame 8875:3776.
 * Layout & chrome match MCP `get_design_context` export (508×68 shell, gap 98, padding 25/6/4).
 */
export type StartPrismInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "size"
> & {
  /** Shows loading chrome on the action control only; does not change the input value or copy. */
  loading?: boolean;
  /** When set, shows helper error copy below the row; does not change input text/caret styling. */
  errorMessage?: string;
  /** Called when the Match My Project button is clicked. */
  onActionClick?: () => void;
  /** Controls disabled state for the Match My Project button. */
  actionDisabled?: boolean;
  prefix?: string;
};

const ARROW_SRC = "/images/landing/start-prism/arrow-icon.svg";
const SPINNER_SRC = "/images/landing/start-prism/spinner-up.svg";

const BTN_DROP =
  "0px 0px 2.696px 0px rgba(0,0,0,0.1), 0px 1.348px 10.783px 0px rgba(0,0,0,0.1)";

const TYPE_LABEL =
  "text-base font-semibold leading-[30.8px] tracking-[-0.3px] [font-feature-settings:'ss01'_1,'cv01'_1,'cv11'_1]";

function PrismStartButton({
  loading,
  disabled,
  onClick,
  className,
}: {
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid="start-prism-input-match-btn"
      className={mergeClasses(
        "relative inline-flex h-[60px] shrink-0 cursor-pointer disabled:cursor-not-allowed items-center rounded-[134.783px] bg-black py-[12px] pl-[28.304px] pr-[8px]",
        "gap-[10.783px]",
        loading ? "border-2 border-solid border-transparent" : "border-0",
        className,
      )}
      style={{
        boxShadow: BTN_DROP,
        ...(loading
          ? {
              backgroundImage:
                "linear-gradient(#000,#000), linear-gradient(90deg, rgba(255,186,186,1) 0%, rgba(146,206,140,1) 40%, rgba(95,155,233,1) 75%, rgba(242,75,201,1) 100%)",
              backgroundOrigin: "padding-box, border-box",
              backgroundClip: "padding-box, border-box",
            }
          : null),
      }}
    >
      {/* Figma uses a second fill with blend LINEAR_BURN; CSS has no equivalent — dual gradients + blur approximate the black pill highlight. */}

      <div
        className={mergeClasses(
          "relative z-10 flex shrink-0 flex-col justify-center leading-0 whitespace-nowrap",
          TYPE_LABEL,
          loading ? "text-[rgba(255,255,255,0.6)]" : "text-white",
        )}
      >
        <span className="leading-[30.8px]">Match My Project</span>
      </div>
      {loading ? (
        <div className="relative z-10 size-[50px] shrink-0">
          <div className="absolute top-[5px] left-[4.91px] flex size-[39.51px] items-center justify-center">
            <div className="animate-spin">
              <div className="-rotate-45">
                <Image
                  src={SPINNER_SRC}
                  alt=""
                  width={28.037}
                  height={27.838}
                  className="block h-[27.838px] w-[28.037px] max-w-none"
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative z-10 size-[50px] shrink-0">
          <Image
            src={ARROW_SRC}
            alt=""
            width={50}
            height={50}
            className="absolute inset-0 block size-full max-w-none"
          />
        </div>
      )}
    </button>
  );
}

export const StartPrismInput = forwardRef<
  HTMLInputElement,
  StartPrismInputProps
>(function StartPrismInput(
  {
    className,
    placeholder,
    loading = false,
    errorMessage,
    onActionClick,
    actionDisabled = false,
    value: valueProp,
    defaultValue,
    autoFocus,
    readOnly,
    disabled,
    prefix,
    id,
    ...inputRest
  },
  ref,
) {
  const hasFilledLook =
    valueProp !== undefined
      ? String(valueProp).length > 0
      : String(defaultValue ?? "").length > 0;

  /** Figma artboards: hover / filled / loading rows use white hairline + outer glow (vs default gray stroke). */
  const shellEmphasis = loading;

  const shellBase = `box-border flex w-full max-w-[550px] items-center rounded-[9999px] bg-[rgba(255,255,255,0.8)] py-[4px] ${prefix ? "pl-[16px]" : "pl-[25px]"} pr-[6px]`;

  const shell = mergeClasses(
    shellBase,
    mergeClasses(
      "gap-[30px]",
      shellEmphasis
        ? "border border-solid border-white shadow-[0px_4px_21.4px_0px_rgba(255,255,255,0.62)]"
        : mergeClasses(
            "border border-solid border-[#e5e7eb]",
            "hover:border-white hover:shadow-[0px_4px_21.4px_0px_rgba(255,255,255,0.62)]",
            "focus-within:border-white focus-within:shadow-[0px_4px_21.4px_0px_rgba(255,255,255,0.62)]",
          ),
    ),
  );

  const valueRow = (
    <>
      <div className="flex min-w-0 flex-1 items-center">
        {prefix ? (
          <span
            aria-hidden="true"
            className="inline-flex h-10 w-6 select-none items-center justify-center text-[16px] font-medium text-v2-content-primary"
          >
            {prefix}
          </span>
        ) : null}
        <input
          ref={ref}
          id={id}
          disabled={disabled}
          autoFocus={autoFocus}
          readOnly={readOnly}
          placeholder={placeholder}
          data-testid="start-prism-input-handle-input"
          {...inputRest}
          {...(valueProp !== undefined
            ? { value: valueProp }
            : { defaultValue })}
          className={mergeClasses(
            "min-w-0 flex-1 bg-transparent outline-none",
            TYPE_LABEL,
            hasFilledLook ? "text-[#1c1916] caret-black" : "text-[#1c1916]",
            "placeholder:text-[#929292]",
          )}
        />
      </div>
      <PrismStartButton
        loading={loading}
        onClick={onActionClick}
        disabled={actionDisabled}
      />
    </>
  );

  if (errorMessage) {
    return (
      <div
        className={mergeClasses(
          "flex w-full max-w-[550px] flex-col items-start gap-2",
          className,
        )}
      >
        <div className={mergeClasses(shell, "w-full")}>{valueRow}</div>
        <div className="flex w-full max-w-[550px] items-center justify-center px-[24px]">
          <p
            data-testid="start-prism-input-error"
            className={mergeClasses(
              "min-w-0 flex-1 leading-0",
              TYPE_LABEL,
              "text-[#f65a5a]",
            )}
          >
            <span className="leading-[30.8px]">{errorMessage}</span>
          </p>
        </div>
      </div>
    );
  }

  return <div className={mergeClasses(shell, className)}>{valueRow}</div>;
});

StartPrismInput.displayName = "StartPrismInput";
